import { useState } from 'react';
import api from 'api';
import { useApiData } from 'lib/api';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { defaultTheme } from 'styles';
import {
  Button,
  EmptyIndicator,
  FlexContainer,
  LinkButton
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader
} from 'components/common/box';
import { Form, FormGroup, Select } from 'components/common/form';
import { TextInput } from 'components/common/form/common';
import { SiteSettingsThemesCreate, SiteSettingsThemesLink } from '.';

const SiteSettingsThemes = () => {
  const { dispatch } = useDispatch();
  const { messages, serverConfig, theme } = useReduxState();

  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const { data, loading, updateData } = useApiData({
    url: '/admin/server-themes?type=site'
  });

  let siteThemes = data?.themes || [];

  siteThemes = siteThemes.sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));

  if (search) {
    siteThemes = siteThemes.filter((theme) =>
      theme.title.toLowerCase().includes(search.toLowerCase())
    );
  }

  const save = async (values) => {
    try {
      setSaving(true);
      await api.put(`/admin/server-config`, {
        data: {
          customThemeId: values.customThemeId || null,
          customThemeEnableEditor: values.customThemeEnableEditor
        }
      });
      if (values.customThemeId) {
        const siteTheme = siteThemes.find(
          (theme) => theme.id === values.customThemeId
        );
        dispatch(actions.updateTheme(siteTheme.id, siteTheme.data));
      } else {
        dispatch(actions.updateTheme(null, {}));
      }
      const response = await api.get('/admin/server-config');
      dispatch(actions.setServerConfig(response.data.data));
      setSaving(false);
      dispatch(
        actions.addNotice({
          text: messages.settingsSavedSuccessfully,
          type: 'success'
        })
      );
    } catch (err) {
      setSaving(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const { customThemeId, customThemeEnableEditor } = serverConfig;

  return (
    <div className="site-themes">
      <Box loading={loading} padding={0} title={messages.siteThemes}>
        {!loading && siteThemes ? (
          <div className="content">
            <div className="save">
              <Form
                initialValues={{
                  customThemeId: customThemeId || 0,
                  customThemeEnableEditor
                }}
                onSubmit={save}
              >
                {({ formState }) => (
                  <div>
                    <FormGroup label={messages.siteThemesTheme}>
                      <FlexContainer style={{ gap: 10 }}>
                        <Select
                          field="customThemeId"
                          id={`custom-theme`}
                          isSearchable={true}
                          options={[
                            {
                              label: messages.commonDefault,
                              value: 0
                            },
                            ...siteThemes.map((siteTheme) => ({
                              label: siteTheme.title,
                              value: siteTheme.id
                            }))
                          ]}
                          style={{ width: 300, maxWidth: '100%' }}
                        />

                        {formState.values?.customThemeId ? (
                          <LinkButton
                            category="lighter"
                            href={`/admin/settings/themes/${customThemeId}`}
                            size="medium"
                            text={'Edit current theme'}
                          />
                        ) : null}
                      </FlexContainer>
                    </FormGroup>
                    {/* <FormGroup label="Theme editor">
                      <Checkbox
                        field="customThemeEnableEditor"
                        id="customThemeEnableEditor"
                        label="Enable theme editor"
                      />
                    </FormGroup> */}
                    <FormGroup>
                      <Button
                        category="success"
                        loading={saving}
                        text={messages.commonSave}
                        type="submit"
                      />
                    </FormGroup>
                  </div>
                )}
              </Form>
            </div>
          </div>
        ) : null}
      </Box>

      <Box
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            <div style={{ width: 240 }}>
              <TextInput
                placeholder={messages.commonSearch}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <SiteSettingsThemesCreate type="site" updateThemes={updateData} />
          </FlexContainer>
        }
        loading={loading}
        padding={0}
        style={{ marginTop: 20 }}
        title="Available themes"
      >
        {siteThemes.length ? (
          <BoxTable>
            <BoxTableHeader>
              <BoxTableCell>{messages.commonTitle}</BoxTableCell>
              <BoxTableCell>{messages.commonUpdated}</BoxTableCell>
              <BoxTableCell>{messages.commonStatus}</BoxTableCell>
              <BoxTableCell />
            </BoxTableHeader>

            {siteThemes.map((siteTheme) => (
              <SiteSettingsThemesLink
                as={`/admin/settings/themes/${siteTheme.id}`}
                href="/admin/settings/themes/[themeId]"
                color={
                  siteTheme.data.subHeaderBackgroundColor ||
                  defaultTheme.subHeaderBackgroundColor
                }
                id={siteTheme.id}
                key={siteTheme.id}
                themeEnabledStatus={
                  customThemeId === siteTheme.id
                    ? 'Default'
                    : siteTheme.data.enableWithAttributeConditions?.enabled
                    ? 'Conditional'
                    : 'Disabled'
                }
                title={siteTheme.title}
                updatedAt={siteTheme.updatedAt}
                updateThemes={updateData}
              />
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="palette" title="No themes found" />
        )}
      </Box>

      <style jsx>{`
        .save {
          padding: 15px;
        }
        .themes {
          border-top: 1px solid ${theme.lightGrey};
          margin-top: 30px;
        }
      `}</style>
    </div>
  );
};

export default SiteSettingsThemes;
